import React, { useEffect } from 'react'
import { Modal } from 'react-bootstrap'
import styled from 'styled-components';
import { Theme } from '../../../../utils/themes'
import { FormGroup } from '../OrganizationsListing'
import StyledText from '../../../../components/StyledComponents/StyledText'
import AMSSelect from '../../../../components/AMSSelect'
import { Button } from '../../../../components/form/Button'
import PlusIcon from '../../../../assets/icons/PlusIcon'
import DeleteIcon from '../../../../assets/icons/DeleteIcon'
import { CheckboxItem } from '../../../../components/listing/ColumnIdentifier'
import StyledToolTip from '../../../../components/StyledComponents/StyledTooltip'
import TooltipIcon from '../../../../assets/icons/Tooltip'
import { ErrorText } from '../../../../components/FormBuilder/FieldSettings/FieldSettingsModal'
import { useTheme } from '../../../../middleware/ThemeContext'
import { LoadingSpinner } from '../../../ui/LoadingSpinner'
import CrossIcon from '../../../../assets/icons/CrossIcon'
import SaveIcon from '../../../../assets/icons/SaveIcon'

interface CreateFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    metadataValues: any;
    OrgAll: any[];
    handleOrgRoleChange: (index: number, field: "org" | "role", value: any) => void;
    addOrganization: (orgName: string) => Promise<number>;
    allOrg: any;
    LoadMoreOrg: () => Promise<void>;
    handleSearch: (query: string) => void;
    RolesAll: any[];
    handleRolesSearch: (query: string) => void;
    metadataErrors: Record<string, string>;
    addOrgRoleRow: () => void;
    removeOrgRoleRow: (index: number) => void;
    handleIsPrimaryChange: (index: number) => void;
    handleSubmit: () => void;
    isFetchingOrg?: boolean;
    isLoadingUpdateOrg?: boolean;
    isLoadingRol?: boolean;
}

const OrganizationModal: React.FC<CreateFormModalProps> = ({ isLoadingUpdateOrg, isFetchingOrg, handleSubmit, handleIsPrimaryChange, removeOrgRoleRow, addOrgRoleRow, metadataErrors, handleRolesSearch, RolesAll, isOpen, onClose, metadataValues, OrgAll, handleOrgRoleChange, addOrganization, allOrg, LoadMoreOrg, handleSearch, isLoadingRol }) => {
    const { theme } = useTheme();
    const normalizeRolesValueForSelect = (rolesValue: any) => {
        if (Array.isArray(rolesValue)) {
            return rolesValue.map((item) => {
                if (typeof item === "object" && item !== null) {
                    const value = item.value ?? item.id ?? item.roleId ?? item;
                    const label = item.label ?? item.name ?? String(value ?? "");
                    return { value, label };
                } else {
                    // Assume it's an ID
                    return { value: item, label: String(item) };
                }
            });
        }
        if (!rolesValue) return [];
        if (rolesValue && typeof rolesValue === "object") {
            const value = (rolesValue as any).value ?? (rolesValue as any).id ?? (rolesValue as any).roleId ?? rolesValue;
            const label = (rolesValue as any).label ?? (rolesValue as any).name ?? String(value ?? "");
            return [{ value, label }];
        }
        return [{ value: rolesValue, label: String(rolesValue ?? "") }];
    };

    return (
        <StyledModal show={isOpen} onHide={onClose} size='lg' centered theme={theme}>
            {isFetchingOrg ? <div style={{ background: theme.CorporateTheme.colors.background }} className=' d-flex align-items-center justify-content-center py-5'>

                <LoadingSpinner
                    size="20px"
                    color={theme.CorporateTheme.colors.heavyMetal}
                />

            </div> : <OrgRoleCont theme={theme}>
                {metadataValues?.organizationRoles?.map((row: any, index: any) => (
                    <div className="row align-items-start" key={index}>
                        {/* Organization */}
                        <div className="col-5">
                            <FormGroup className="mb-0">
                                <FieldLabel theme={theme} className="mb-1">
                                    Organization Name
                                </FieldLabel>
                                <AMSSelect
                                    endpoint="/organizations/minimal"
                                    placeholder="Select Organization"
                                    value={row.organizationId ? { id: row.organizationId, value: row.organizationId, label: (row as any).organizationName || String(row.organizationId) } : null}
                                    onChange={(option) => handleOrgRoleChange(index, "org", option)}
                                    getOptionLabel={(option: any) => option?.name ?? option?.label ?? ""}
                                    defaultOptions
                                    includeId
                                    includeImage
                                    includeAddButton
                                    addButtonText="Create Organization"
                                    onAddButtonClick={async (val: string) => {
                                        const newOrgId = await addOrganization(val);
                                        if (newOrgId) {
                                            handleOrgRoleChange(index, "org", { id: newOrgId, name: val });
                                        }
                                    }}
                                />
                            </FormGroup>
                        </div>
                        {row?.organizationId && (
                            <div className="col-5">
                                <FormGroup className="mb-0">
                                    <FieldLabel theme={theme} className="mb-1">
                                        Contact Roles
                                    </FieldLabel>
                                    <AMSSelect
                                        endpoint="/contact-roles"
                                        placeholder="Select Roles"
                                        isMulti
                                        defaultOptions
                                        value={normalizeRolesValueForSelect(row.rolesId)}
                                        onChange={(options) => handleOrgRoleChange(index, "role", Array.isArray(options) ? options : options ? [options] : [])}
                                        mapResponse={(json: any, page: number) => {
                                            const roles = json?.roles || [];
                                            const totalCount = json?.totalCount ?? 0;
                                            const perPage = json?.perPage ?? json?.pagination?.perPage ?? 10;
                                            const hasMore = totalCount ? page * perPage < totalCount : roles.length === perPage;
                                            const options = roles.map((role: any) => ({
                                                label: role?.name ?? role?.label ?? String(role?.id ?? ""),
                                                value: role?.id ?? role?.value ?? role,
                                                ...role,
                                            }));
                                            return { options, hasMore, additional: { page: page + 1 } };
                                        }}
                                    />
                                    {metadataErrors["organizationRoles_role_" + index] && (
                                        <ErrorText theme={theme}>
                                            {metadataErrors["organizationRoles_role_" + index]}
                                        </ErrorText>
                                    )}
                                </FormGroup>
                            </div>
                        )}
                        <div className="col-2 d-flex align-items-center gap-2">
                            {index === metadataValues.organizationRoles.length - 1 ? (
                                <Button
                                    type="button"
                                    theme={theme}
                                    $variant="customize"
                                    onClick={addOrgRoleRow}
                                    style={{ marginTop: '20px' }}
                                >
                                    <PlusIcon />{" "}
                                </Button>
                            ) : (
                                <Button
                                    type="button"
                                    theme={theme}
                                    $variant="customize"
                                    onClick={() => removeOrgRoleRow(index)}
                                    style={{ marginTop: '20px' }}
                                >
                                    <DeleteIcon />{" "}
                                </Button>
                            )}
                        </div>
                        <div className="col-12">
                            <div className="d-flex align-items-center gap-2 mt-3">
                                <CheckboxItem theme={theme}>
                                    <input
                                        type="radio"
                                        name="isPrimary"
                                        checked={row.isPrimary || false}
                                        onChange={() => handleIsPrimaryChange(index)}
                                    />
                                </CheckboxItem>
                                <FieldLabel theme={theme}>
                                    Mark this organization as primary
                                </FieldLabel>
                                <div className="flex-shrink-0">
                                    <StyledToolTip
                                        placement="top"
                                        title={'Contact Affiliation to Organization'}
                                    >
                                        <TooltipIcon
                                            size={24}
                                            color={theme.CorporateTheme.colors.textSecondary}
                                        />
                                    </StyledToolTip>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}

                <div className='d-flex align-items-center justify-content-end gap-3 mt-3'>
                    <Button
                        theme={theme}
                        btnBg={theme?.CorporateTheme?.colors?.filterBG}
                        color={theme?.CorporateTheme?.colors?.textSecondary}
                        type="button"
                        $variant="customButton"
                        className="px-3"
                        onClick={onClose}
                    >
                        <CrossIcon />
                        Cancel
                    </Button>
                    <Button
                        theme={theme}
                        btnBg={theme?.CorporateTheme?.colors?.primary}
                        color={theme?.CorporateTheme?.colors?.background}
                        type="button"
                        $variant="customButton"
                        className={`px-3`}
                        onClick={handleSubmit}
                        disabled={isLoadingUpdateOrg}
                    >
                        {isLoadingUpdateOrg ? <LoadingSpinner size='16px' color={theme?.CorporateTheme?.colors?.background} /> : <SaveIcon color={theme?.CreateFormTheme?.colors?.background} />}
                        Save
                    </Button>
                </div>
            </OrgRoleCont>}
        </StyledModal>
    )
}

const StyledModal = styled(Modal) <{ theme: Theme }>`
    .modal-dialog{
        max-width: 696px;
        align-self: center;
        
    }
`;

const OrgRoleCont = styled("div")`
padding: 30px;
background-color: ${({ theme }) => theme.CorporateTheme.colors.background};

  & .row {
    padding: 20px 0px;
  }
  & .row:not(:last-child) {
    border-bottom: 1px solid
      ${({ theme }) => theme.CorporateTheme.colors.border};
  }
`;

export const FieldLabel = styled(StyledText)`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.CorporateTheme.colors.textSecondary};
`;
export default OrganizationModal
